import {
  LoginResponseSchema,
  TokenPairSchema,
  AuthenticatedUserSchema,
  type LoginRequest,
  type LoginResponse,
  type LogoutReason,
  type SessionEventType,
  type TokenPair,
  type AuthenticatedUser,
} from '@utazzo/shared';
import axios from 'axios';

import { env } from '@/env';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Auth API surface. Each function validates the server response with the
 * shared Zod schema - if the server ever drifts from the contract we throw
 * here rather than letting bad data leak into the store.
 */

export const login = async (payload: LoginRequest): Promise<LoginResponse> => {
  const { data } = await apiClient.post<unknown>('/auth/login', payload);
  return LoginResponseSchema.parse(data);
};

export const refresh = async (refreshToken: string): Promise<TokenPair> => {
  const { data } = await apiClient.post<unknown>('/auth/refresh', { refreshToken });
  return TokenPairSchema.parse(data);
};

/**
 * End this device's session on the server. The tokens are read the moment
 * this is called - before its first await - because idle logout clears the
 * store straight after calling it (it never holds the redirect for the
 * network), and axios only runs the Bearer interceptor a tick later. Without
 * that the request went out tokenless, the session was never revoked, and
 * the browser lingered in "Where you're signed in" (and round-robin's
 * "online" check) until the refresh token expired.
 */
export const logout = async (reason?: LogoutReason): Promise<void> => {
  const tokens = useAuthStore.getState().tokens;
  const body = reason ? { reason } : {};
  try {
    await apiClient.post(
      '/auth/logout',
      body,
      tokens ? { headers: { Authorization: `Bearer ${tokens.accessToken}` } } : undefined,
    );
  } catch (err) {
    // After a long idle the access token has usually expired, and with the
    // store already cleared the client's own silent refresh has nothing to
    // use. Refresh once with the captured token (bare axios: no interceptors,
    // nothing written to the store), then end that session.
    const status = (err as { statusCode?: unknown } | null)?.statusCode;
    if (status !== 401 || !tokens?.refreshToken || useAuthStore.getState().tokens) throw err;
    const { data } = await axios.post<unknown>(`${env.VITE_API_BASE_URL}/auth/refresh`, {
      refreshToken: tokens.refreshToken,
    });
    const fresh = TokenPairSchema.parse(data);
    await axios.post(`${env.VITE_API_BASE_URL}/auth/logout`, body, {
      headers: { Authorization: `Bearer ${fresh.accessToken}` },
    });
  }
};

/**
 * Report a client-only session event (idle warning shown / user extended) so
 * the SERVER can record it in the audit trail with the real IP / time / geo.
 * Audit-only + best-effort - callers should not block UX on it.
 */
export const recordSessionEvent = async (type: SessionEventType): Promise<void> => {
  await apiClient.post('/auth/session-events', { type });
};

export const forgotPassword = async (email: string): Promise<void> => {
  await apiClient.post('/auth/forgot-password', { email });
};

export const resetPassword = async (payload: {
  token: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<void> => {
  await apiClient.post('/auth/reset-password', payload);
};

export const me = async (): Promise<AuthenticatedUser> => {
  const { data } = await apiClient.get<unknown>('/auth/me');
  return AuthenticatedUserSchema.parse(data);
};
